import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from '../utils/toast';
import { api, staffAPI } from '../services/api';

const invalidateAll = (queryClient) =>
  queryClient.invalidateQueries({ queryKey: ['courseRequests'] });

const normalizePaginatedResponse = (data) => {
  if (Array.isArray(data)) {
    return { count: data.length, next: null, previous: null, results: data };
  }
  return {
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results: data?.results ?? [],
  };
};

export const useCourseRequestCapabilities = (options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'capabilities'],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequestCapabilities();
      return response.data || {};
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCourseRequests = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'list', params],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequests({
        page_size: 500,
        ...params,
      });
      return response.data?.results || response.data || [];
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const useMyCourseRequests = (options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'my'],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequestMyRequests();
      return response.data?.results || response.data || [];
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const useCourseRequestPendingHod = (options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'pendingHod'],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequestPendingHod();
      return response.data || [];
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const useCourseRequestHrInbox = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'hrInbox', params],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequestHrInbox({
        page_size: 500,
        ...params,
      });
      return response.data?.results || response.data || [];
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const usePaginatedCourseRequests = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'paginatedList', params],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequests(params);
      return normalizePaginatedResponse(response.data);
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const usePaginatedCourseRequestHrInbox = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'paginatedHrInbox', params],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequestHrInbox(params);
      return normalizePaginatedResponse(response.data);
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const useCourseRequestStatusSummary = (options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'statusSummary'],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequestStatusSummary();
      return response.data || {};
    },
    staleTime: 60 * 1000,
    ...options,
  });

export const useCourseRequestBondManagement = (options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'bondManagement'],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequestBondManagement();
      return response.data || {
        confirmed: [],
        review_candidates: [],
        summary: {},
      };
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCourseRequestDetail = (id, options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'detail', id],
    queryFn: async () => {
      const response = await staffAPI.getCourseRequest(id);
      return response.data;
    },
    enabled: !!id,
    ...options,
  });

export const useNominatableCourseWorkers = (options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'workers'],
    queryFn: async () => {
      const response = await staffAPI.getCourseNominatableWorkers();
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useMasterCourses = (options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'masterCourses'],
    queryFn: async () => {
      const response = await api.get('/configuration/course-library/master-courses/active/');
      return response.data?.results || response.data || [];
    },
    staleTime: 10 * 60 * 1000,
    ...options,
  });

export const useCourseLibrary = (options = {}) =>
  useQuery({
    queryKey: ['courseLibrary', 'masterCourses', 'withProviders'],
    queryFn: async () => {
      const response = await api.get(
        '/configuration/course-library/master-courses/list_with_providers/',
      );
      return response.data || [];
    },
    staleTime: 10 * 60 * 1000,
    ...options,
  });

const invalidateCourseLibrary = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['courseLibrary'] });
  queryClient.invalidateQueries({ queryKey: ['courseRequests', 'masterCourses'] });
};

export const useCreateMasterCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post('/configuration/course-library/master-courses/', data),
    onSuccess: () => {
      invalidateCourseLibrary(queryClient);
      toast.success('Master course created');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.code?.[0] || error?.response?.data?.detail || 'Failed to create master course');
    },
  });
};

export const useUpdateMasterCourse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, data }) => api.patch(`/configuration/course-library/master-courses/${code}/`, data),
    onSuccess: () => {
      invalidateCourseLibrary(queryClient);
      toast.success('Master course updated');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.code?.[0] || error?.response?.data?.detail || 'Failed to update master course');
    },
  });
};

export const useTrainingProviders = (options = {}) =>
  useQuery({
    queryKey: ['courseRequests', 'providers'],
    queryFn: async () => {
      const response = await api.get('/configuration/training-providers/', {
        params: { page_size: 10000 },
      });
      return response.data?.results || response.data || [];
    },
    staleTime: 10 * 60 * 1000,
    ...options,
  });

export const useCreateCourseRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.createCourseRequest(data),
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success('Course request submitted');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to submit course request');
    },
  });
};

export const useCourseRequestDecision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, comments }) => {
      const map = {
        'approve-hod': staffAPI.approveCourseRequestHod,
        'reject-hod': staffAPI.rejectCourseRequestHod,
        'approve-hr': staffAPI.approveCourseRequestHr,
        'reject-hr': staffAPI.rejectCourseRequestHr,
        cancel: staffAPI.cancelCourseRequest,
      };
      const fn = map[action];
      if (!fn) throw new Error(`Unknown action ${action}`);
      return fn(id, { comments, reason: comments });
    },
    onSuccess: (_data, vars) => {
      invalidateAll(queryClient);
      const labels = {
        'approve-hod': 'Approved (HOD)',
        'reject-hod': 'Rejected (HOD)',
        'approve-hr': 'Approved (HR)',
        'reject-hr': 'Rejected (HR)',
        cancel: 'Cancelled',
      };
      toast.success(labels[vars.action] || 'Updated');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Action failed');
    },
  });
};

export const useIssueSponsorshipAgreement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.issueCourseAgreement(id, data),
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success('Sponsorship agreement issued — awaiting employee signature');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to issue sponsorship agreement');
    },
  });
};

export const useAcknowledgeSponsorshipAgreement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.acknowledgeCourseAgreement(id, data),
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success('Sponsorship agreement signed');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to sign sponsorship agreement');
    },
  });
};

export const useConfirmCourseBooking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }) => staffAPI.confirmCourseRequestBooking(id, formData),
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success('Booking confirmed');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to confirm booking');
    },
  });
};

export const useCompleteCourseRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }) => staffAPI.completeCourseRequest(id, formData),
    onSuccess: () => {
      invalidateAll(queryClient);
      toast.success('Result recorded');
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to record result');
    },
  });
};
